import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Badge,
  Tooltip,
  Chip,
  Paper
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  ViewModule as ViewModuleIcon,
  ViewList as ViewListIcon,
  SmartToy as SmartToyIcon,
  AutoAwesome as AutoAwesomeIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';
import AgentCard from './AgentCard';
import AgentListView from './AgentListView';

const AgentOverview = ({ 
  onLaunchAgent, 
  activeAgents = [], 
  onRefresh, 
  onDelete, 
  isLoading 
}) => {
  const theme = useTheme();
  const [viewMode, setViewMode] = useState('cards');

  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      setViewMode(newView);
    }
  };

  const getActiveCount = () => activeAgents.filter(agent => agent.isActive).length;
  const getInactiveCount = () => activeAgents.filter(agent => !agent.isActive).length;

  const renderHeaderSection = () => (
    <Paper sx={{ 
      p: 2, 
      mb: 3, 
      bgcolor: 'background.paper', 
      borderRadius: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SmartToyIcon sx={{ color: theme.palette.primary.main }} />
          <Typography variant="h6">
            AI Agents
          </Typography>
          <Chip 
            label={`${activeAgents.length} total`} 
            size="small" 
            sx={{ ml: 1 }}
          />
          {getActiveCount() > 0 && (
            <Chip 
              label={`${getActiveCount()} active`} 
              size="small" 
              color="success"
              sx={{ height: 20 }}
            />
          )}
          {getInactiveCount() > 0 && (
            <Chip 
              label={`${getInactiveCount()} inactive`} 
              size="small" 
              color="warning"
              sx={{ height: 20 }}
            />
          )}
        </Box>
      </Box>
      
      {onLaunchAgent && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={onLaunchAgent}
            sx={{
              bgcolor: theme.palette.primary.main,
              color: 'black',
              '&:hover': {
                bgcolor: theme.palette.primary.dark
              }
            }}
          >
            Build Agent
          </Button>
        </Box>
      )}
    </Paper>
  );

  const renderControlsSection = () => (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center', 
      mb: 2 
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={handleViewChange}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              color: 'text.secondary',
              borderColor: 'divider',
              py: 0.5,
              '&.Mui-selected': {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.mode === 'dark' ? theme.palette.common.black : theme.palette.common.black,
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                }
              },
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
              }
            }
          }}
        >
          <ToggleButton value="cards" aria-label="card view">
            <ViewModuleIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton value="list" aria-label="list view">
            <ViewListIcon fontSize="small" />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      
      <Tooltip title={isLoading ? "Refreshing..." : "Refresh agents"}>
        <IconButton 
          onClick={onRefresh} 
          disabled={isLoading}
          size="small"
        >
          <Badge variant="dot" color="success" invisible={!isLoading}>
            <RefreshIcon />
          </Badge>
        </IconButton>
      </Tooltip>
    </Box>
  );

  const renderAgentsList = () => {
    if (isLoading && activeAgents.length === 0) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 8 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (activeAgents.length === 0) {
      return (
        <Box sx={{ 
          textAlign: 'center', 
          py: 8, 
          backgroundColor: 'background.paper', 
          borderRadius: 2 
        }}>
          <AutoAwesomeIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
            No agents found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {onLaunchAgent ? 'Get started by building your first AI agent' : 'No agents assigned to you yet'}
          </Typography>
          {onLaunchAgent && (
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={onLaunchAgent}
              >
                Build Agent
              </Button>
            </Box>
          )}
        </Box>
      );
    }

    if (viewMode === 'cards') {
      return (
        <Grid container spacing={2}>
          {activeAgents.map((agent) => (
            <Grid item xs={12} sm={6} md={4} lg={4} key={agent.id}>
              <AgentCard 
                agent={agent} 
                onDelete={onDelete}
              />
            </Grid>
          ))}
        </Grid>
      );
    }

    return (
      <AgentListView 
        agents={activeAgents}
        onDelete={onDelete}
      />
    );
  };

  return (
    <Box>
      {renderHeaderSection()}
      {activeAgents.length > 0 && renderControlsSection()}
      {renderAgentsList()}
    </Box>
  );
};

AgentOverview.propTypes = {
  onLaunchAgent: PropTypes.func,
  activeAgents: PropTypes.array,
  onRefresh: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  isLoading: PropTypes.bool
};

export default AgentOverview; 